import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { map, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import {
  AttributeResponse,
  TelemetryResponse,
  TelemetryResponseDTO,
  TelemetryEntry,
} from "../../../models/shared/telemetry";

@Injectable({
  providedIn: "root",
})
export class TelemetryService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Get telemetry attributes for a given asset.
   *
   * @param assetId The id of the asset to query.
   * @param assetType The type of the asset to query.
   * @param keys The keys of the attributes to query.
   * @returns An observable with the response from the server.
   */
  public getAttributes(assetId: string, assetType: string, keys: string) {
    const params: { assetId?: string; assetType?: string; keys?: string } = {};
    params.assetId = assetId;
    params.assetType = assetType;
    params.keys = keys;

    return this.http
      .get<AttributeResponse>(environment.baseUrl + "/attributes", {
        params,
      })
      .pipe(this.apiErrorHandler.handle("attribute"));
  }
  /**
   * Retrieves timeseries telemetry data for a given asset.
   *
   * @param assetId - The ID of the asset to retrieve timeseries data for.
   * @param assetType - The type of the asset (e.g., building, gate system).
   * @param keys - Comma-separated telemetry keys to retrieve.
   * @param startTs - (Optional) The start timestamp for the data retrieval.
   * @param endTs - (Optional) The end timestamp for the data retrieval.
   * @param order - (Optional) The order of data retrieval (e.g., ASC, DESC).
   * @returns An observable that emits the telemetry response.
   */

  public getTimeseries(
    assetId: string,
    assetType: string,
    keys: string,
    startTs?: number,
    endTs?: number,
    order?: string
  ): Observable<TelemetryResponse> {
    const params: {
      assetId?: string;
      assetType?: string;
      keys?: string;
      startTs?: number;
      endTs?: number;
      order?: string;
    } = {};
    params.assetId = assetId;
    params.assetType = assetType;
    params.keys = keys;
    if (startTs) params.startTs = startTs;
    if (endTs) params.endTs = endTs;
    if (order) params.order = order;

    return this.http
      .get<TelemetryResponseDTO>(environment.baseUrl + "/timeseries", {
        observe: "response",
        params,
      })
      .pipe(
        this.apiErrorHandler.handle("attribute"),
        map((res) => {
          const telemetryResponse: TelemetryResponseDTO | null = res.body;
          if (telemetryResponse) {
            Object.keys(telemetryResponse).forEach((key: string) => {
              if (telemetryResponse[key]) {
                telemetryResponse[key] = this.convertToArray(telemetryResponse[key]);
              }
            });
          }
          return telemetryResponse as TelemetryResponse;
        })
      );
  }

  // Temporary: Just doing this because Backend doesnt return response as an array if there is only one point:
  // Remove this when BE is fixed
  private convertToArray(obj: TelemetryEntry | TelemetryEntry[] | undefined): TelemetryEntry[] {
    if (!obj) {
      return [];
    }
    if (!Array.isArray(obj)) {
      obj = [obj];
    }
    return obj;
  }
}
